import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { NeuButton } from "../../components/neu/Neu";
import { Field } from "../../components/neu/Field";
import * as api from "../../api/timeOff";
import { TIME_OFF_TYPES } from "./timeOffTypes";
import { autoTimes } from "./timeOffTime";

const initial = { time_off_type: "start_delay", request_date: "", hours: 1, start_time: "", end_time: "", reason: "" };

// نموذج طلب إجازة زمنية. وقت العودة/الانصراف يُحسب تلقائياً ويُثبّت (عدا mid_day اليدوي).
export const TimeOffForm = ({ balance, onDone }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const disabled = !balance?.can_request;
  const ws = balance?.work_start || "09:00";
  const we = balance?.work_end || "17:00";
  const isMid = form.time_off_type === "mid_day";
  const autoTime = form.time_off_type === "start_delay" ? form.end_time : form.start_time;

  useEffect(() => {
    const auto = autoTimes(form.time_off_type, form.hours, ws, we);
    setForm((f) => (auto ? { ...f, ...auto } : { ...f, start_time: "", end_time: "" }));
  }, [form.time_off_type, form.hours, ws, we]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.create({ ...form, hours: Number(form.hours) });
      toast.success(t("timeOff.created"));
      setForm(initial);
      onDone?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || {};
      const code = detail.code || "TIME_OFF_GENERIC";
      toast.error(t(`timeOff.errors.${code}`, { date: detail.date, defaultValue: t("errors.generic") }));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid gap-4" data-testid="time-off-form">
      <div className="grid grid-cols-3 gap-2">
        {TIME_OFF_TYPES.map(({ key, icon: Icon, color }) => {
          const active = form.time_off_type === key;
          return (
            <button key={key} type="button" data-testid={`time-off-type-${key}`}
              onClick={() => set("time_off_type", key)}
              className={`flex flex-col items-center gap-1 rounded-3xl bg-background px-2 py-3 transition ${active ? "shadow-neu-in text-primary" : "shadow-neu-sm text-foreground"}`}>
              <span className={`flex h-10 w-10 items-center justify-center rounded-full text-white shadow-neu-sm ${color}`}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-[11px] font-bold">{t(`timeOff.type.${key}`)}</span>
            </button>
          );
        })}
      </div>
      <Field label={t("timeOff.requestDate")} testId="time-off-date" type="date"
             value={form.request_date} onChange={(v) => set("request_date", v)} accent="sky" />
      <div className="grid grid-cols-2 gap-2">
        {[1, 2].map((h) => (
          <button key={h} type="button" data-testid={`time-off-hours-${h}`}
            onClick={() => set("hours", h)}
            className={`rounded-2xl bg-background py-2 text-sm font-bold ${form.hours === h ? "shadow-neu-in text-primary" : "shadow-neu-sm"}`}>
            {t("timeOff.hoursValue", { count: h })}
          </button>
        ))}
      </div>
      {isMid ? (
        <div className="grid grid-cols-2 gap-2">
          <Field label={t("timeOff.startTime")} testId="time-off-start" type="time"
                 value={form.start_time} onChange={(v) => set("start_time", v)} accent="emerald" />
          <Field label={t("timeOff.endTime")} testId="time-off-end" type="time"
                 value={form.end_time} onChange={(v) => set("end_time", v)} accent="violet" />
        </div>
      ) : (
        <div className="rounded-2xl bg-background p-3 shadow-neu-in" data-testid="time-off-auto-time">
          <p className="text-xs text-muted-foreground">
            {form.time_off_type === "start_delay" ? t("timeOff.returnTime") : t("timeOff.leaveTime")}
          </p>
          <p className="mt-1 font-mono text-2xl font-black text-primary" data-testid="time-off-auto-value">{autoTime || "—"}</p>
          {form.time_off_type === "early_leave" ? (
            <p className="mt-1 text-[11px] text-muted-foreground">{t("timeOff.autoNote")}</p>
          ) : null}
        </div>
      )}
      <Field label={t("timeOff.reason")} testId="time-off-reason"
             value={form.reason} onChange={(v) => set("reason", v)} accent="cyan" />
      <NeuButton variant="primary" type="submit" data-testid="time-off-submit" disabled={busy || disabled}>
        {t("timeOff.submit")}
      </NeuButton>
    </form>
  );
};
